import React, { useState, useEffect } from "react";
import {
  Search,
  Calendar,
  X,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Printer,
} from "lucide-react";
// import '../../styles/Admin.css';
import "./OrderHistoryModal.css"; // Assuming you have a CSS file for styling

const OrderHistoryModal = ({ show, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    if (show) {
      fetchOrders();
    }
  }, [show]);

  const fetchOrders = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        limit: 1000000,
        ...params,
      }).toString();

      const response = await fetch(
        `https://dat-order-history.t9e2i3kal.workers.dev/orders?${query}`,
        {
          headers: {
            "X-Auth-Guid": "17225c0b-2167-46c8-bba9-506f5b391813",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setOrders(data.orders);
      } else {
        throw new Error(data.message || "Failed to fetch orders");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (dateRange.startDate) params.startDate = dateRange.startDate;
    if (dateRange.endDate) params.endDate = dateRange.endDate;
    fetchOrders(params);
  };

  const handleReset = () => {
    setSearchTerm("");
    setDateRange({ startDate: "", endDate: "" });
    fetchOrders();
  };

  const getDateString = (date) => {
    return date.toISOString().split("T")[0];
  };

  const filterLast7Days = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const startStr = getDateString(sevenDaysAgo);
    const endStr = getDateString(today);
    setDateRange({ startDate: startStr, endDate: endStr });
    const params = { startDate: startStr, endDate: endStr };
    if (searchTerm) params.search = searchTerm;
    fetchOrders(params);
  };

  const filterLastMonth = () => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDayOfLastMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      0
    );
    const startStr = getDateString(lastMonth);
    const endStr = getDateString(lastDayOfLastMonth);
    setDateRange({ startDate: startStr, endDate: endStr });
    const params = { startDate: startStr, endDate: endStr };
    if (searchTerm) params.search = searchTerm;
    fetchOrders(params);
  };

  const printSummary = () => {
    const printWindow = window.open("", "_blank");
    const currentDate = new Date().toLocaleDateString();

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order Summary Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .stats {
              display: flex;
              justify-content: space-around;
              margin-bottom: 30px;
            }
            .stat {
              text-align: center;
              padding: 15px;
              border: 1px solid #ddd;
              border-radius: 5px;
            }
            .stat-value {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
            }
            .stat-label {
              font-size: 14px;
              color: #666;
              margin-bottom: 5px;
            }
            .section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .section h3 {
              border-bottom: 1px solid #ddd;
              padding-bottom: 5px;
              margin-bottom: 15px;
            }
            .item-list {
              display: grid;
              grid-template-columns: 1fr auto;
              gap: 10px;
            }
            .item {
              display: contents;
            }
            .item-name {
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            .item-count {
              padding: 8px 12px;
              background: #f8f9fa;
              border-radius: 4px;
              font-weight: bold;
              text-align: center;
              border-bottom: 1px solid #eee;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Order Summary Report</h1>
            <p>Generated on ${currentDate}</p>
            ${
              dateRange.startDate || dateRange.endDate
                ? `<p>Date Range: ${dateRange.startDate || "All time"} to ${
                    dateRange.endDate || "Present"
                  }</p>`
                : "<p>All Orders</p>"
            }
          </div>
          
          <div class="stats">
            <div class="stat">
              <div class="stat-label">Total Orders</div>
              <div class="stat-value">${summary.totalOrders}</div>
            </div>
            <div class="stat">
              <div class="stat-label">Total Revenue</div>
              <div class="stat-value">${summary.totalRevenue}</div>
            </div>
          </div>
          
          <div class="section">
            <h3>Orders by Type</h3>
            <div class="item-list">
              ${Object.entries(summary.typeCounts)
                .map(
                  ([type, count]) => `
                <div class="item">
                  <div class="item-name">${
                    type.charAt(0).toUpperCase() + type.slice(1)
                  }</div>
                  <div class="item-count">${count}</div>
                </div>
              `
                )
                .join("")}
            </div>
          </div>
          
          <div class="section">
            <h3>Item Counts</h3>
            <div class="item-list">
              ${Object.entries(summary.itemCounts)
                .sort(([, a], [, b]) => b - a)
                .map(
                  ([item, count]) => `
                  <div class="item">
                    <div class="item-name">${item}</div>
                    <div class="item-count">${count}</div>
                  </div>
                `
                )
                .join("")}
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const toggleExpandOrder = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const generateItemSummary = () => {
    const itemCounts = {};
    const typeCounts = {};
    let totalRevenue = 0;
    let dogTagCount = 0;

    orders.forEach((order) => {
      // Count order types
      const orderType = order.data?.type || "Unknown";
      typeCounts[orderType] = (typeCounts[orderType] || 0) + 1;

      // Add to total revenue
      totalRevenue += order.data?.total || 0;

      // Count individual items
      if (order.data?.items && Array.isArray(order.data.items)) {
        order.data.items.forEach((item) => {
          // Check for Dog Tags first
          if (item.includes("Dog Tag:")) {
            dogTagCount++;
            itemCounts["Dog Tag"] = (itemCounts["Dog Tag"] || 0) + 1;
          }
          // For photo orders, items might be URLs, so let's handle them differently
          else if (orderType === "photo" && item.startsWith("http")) {
            const photoKey = "Photo/Image File";
            itemCounts[photoKey] = (itemCounts[photoKey] || 0) + 1;
          } else {
            itemCounts[item] = (itemCounts[item] || 0) + 1;
          }
        });
      }
    });

    return {
      itemCounts,
      typeCounts,
      totalRevenue,
      totalOrders: orders.length,
      dogTagCount,
    };
  };

  const summary = generateItemSummary();

  return (
    <div className={`OrderHistoryModal ${show ? "show" : ""}`}>
      <div className="OrderHistoryModal-content">
        <div className="OrderHistoryModal-header">
          <h2 className="OrderHistoryModal-title">Order History</h2>
          <button className="OrderHistoryModal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="OrderHistoryModal-search">
          <div className="OrderHistoryModal-search-group">
            <div className="OrderHistoryModal-search-input">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            <button
              className="OrderHistoryModal-button secondary"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <Calendar size={16} />
              {showDatePicker ? "Hide Dates" : "Filter by Date"}
            </button>

            <button
              className={`OrderHistoryModal-button ${
                showSummary ? "active" : "secondary"
              }`}
              onClick={() => setShowSummary(!showSummary)}
            >
              <BarChart3 size={16} />
              {showSummary ? "Hide Summary" : "Show Summary"}
            </button>
          </div>

          <div className="OrderHistoryModal-quick-filters">
            <span className="OrderHistoryModal-quick-filters-label">
              Quick Filters:
            </span>
            <button
              className="OrderHistoryModal-button secondary small"
              onClick={filterLast7Days}
            >
              Last 7 Days
            </button>
            <button
              className="OrderHistoryModal-button secondary small"
              onClick={filterLastMonth}
            >
              Last Month
            </button>
          </div>

          {showDatePicker && (
            <div className="OrderHistoryModal-date-range">
              <div className="OrderHistoryModal-date-input">
                <label>From:</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, startDate: e.target.value })
                  }
                />
              </div>
              <div className="OrderHistoryModal-date-input">
                <label>To:</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, endDate: e.target.value })
                  }
                />
              </div>
            </div>
          )}

          <div className="OrderHistoryModal-search-buttons">
            <button className="OrderHistoryModal-button" onClick={handleSearch}>
              Search
            </button>
            <button
              className="OrderHistoryModal-button secondary"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>

        {showSummary && (
          <div className="OrderHistoryModal-summary">
            <div className="OrderHistoryModal-summary-header">
              <h3 className="OrderHistoryModal-summary-title">Order Summary</h3>
              <button
                className="OrderHistoryModal-button secondary small"
                onClick={printSummary}
              >
                <Printer size={14} />
                Print Summary
              </button>
            </div>

            <div className="OrderHistoryModal-summary-stats">
              <div className="OrderHistoryModal-stat">
                <div className="OrderHistoryModal-stat-label">Total Orders</div>
                <div className="OrderHistoryModal-stat-value">
                  {summary.totalOrders}
                </div>
              </div>
              <div className="OrderHistoryModal-stat">
                <div className="OrderHistoryModal-stat-label">
                  Total Revenue
                </div>
                <div className="OrderHistoryModal-stat-value">
                  ${summary.totalRevenue}
                </div>
              </div>
              <div className="OrderHistoryModal-stat">
                <div className="OrderHistoryModal-stat-label">Dog Tags</div>
                <div className="OrderHistoryModal-stat-value">
                  {summary.dogTagCount}
                </div>
              </div>
            </div>

            <div className="OrderHistoryModal-summary-grid">
              <div className="OrderHistoryModal-summary-section">
                <h4 className="OrderHistoryModal-summary-section-title">
                  Orders by Type
                </h4>
                <div className="OrderHistoryModal-summary-list">
                  {Object.entries(summary.typeCounts).map(([type, count]) => (
                    <div key={type} className="OrderHistoryModal-summary-item">
                      <span className="OrderHistoryModal-summary-item-name">
                        {type}
                      </span>
                      <span className="OrderHistoryModal-summary-item-count">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="OrderHistoryModal-summary-section">
                <h4 className="OrderHistoryModal-summary-section-title">
                  Item Counts
                </h4>
                <div className="OrderHistoryModal-summary-list OrderHistoryModal-summary-scrollable">
                  {Object.entries(summary.itemCounts)
                    .sort(([, a], [, b]) => b - a) // Sort by count descending
                    .map(([item, count]) => (
                      <div
                        key={item}
                        className="OrderHistoryModal-summary-item"
                      >
                        <span
                          className="OrderHistoryModal-summary-item-name"
                          title={item}
                        >
                          {item}
                        </span>
                        <span className="OrderHistoryModal-summary-item-count">
                          {count}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {loading && <div className="loading-message">Loading orders...</div>}

        {error && <div className="error-message">Error: {error}</div>}

        <div className="OrderHistoryModal-body">
          {!loading && !error && (
            <div className="OrderHistoryModal-orders">
              {orders.length === 0 ? (
                <div className="OrderHistoryModal-empty">No orders found</div>
              ) : (
                <table className="OrderHistoryModal-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <React.Fragment key={order.id}>
                        <tr
                          className="OrderHistoryModal-order-row"
                          onClick={() => toggleExpandOrder(order.id)}
                        >
                          <td>{order.id}</td>
                          <td>{formatDate(order.dateAdded)}</td>
                          <td>{order.data?.type || "Unknown"}</td>
                          <td>${order.data?.total || 0}</td>
                          <td className="OrderHistoryModal-expand">
                            {expandedOrder === order.id ? (
                              <ChevronUp size={20} />
                            ) : (
                              <ChevronDown size={20} />
                            )}
                          </td>
                        </tr>
                        {expandedOrder === order.id && (
                          <tr className="OrderHistoryModal-details-row">
                            <td colSpan="5">
                              <div className="OrderHistoryModal-details">
                                <div className="OrderHistoryModal-detail">
                                  <strong>Customer:</strong>{" "}
                                  {order.data?.customer ||
                                    order.data?.email ||
                                    "N/A"}
                                </div>
                                <div className="OrderHistoryModal-detail">
                                  <strong>Items:</strong>{" "}
                                  {order.data?.items?.length || 0}
                                </div>
                                <div className="OrderHistoryModal-detail">
                                  <strong>Notes:</strong>{" "}
                                  {order.data?.notes || "None"}
                                </div>
                                <div className="OrderHistoryModal-detail">
                                  <strong>Last Modified:</strong>{" "}
                                  {formatDate(order.lastModified)}
                                </div>
                                <div className="OrderHistoryModal-detail">
                                  <strong>Full Data:</strong>
                                  <pre className="OrderHistoryModal-data">
                                    {JSON.stringify(order.data, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistoryModal;
